// ============================================================================
// REGISTRATION TYPE FORM MODAL
// ============================================================================
// Add / Edit form modal for the RegistrationType master — the per-jurisdiction
// list of statutory id types (PAN, GSTIN, CIN, TAN, BURS-VAT, OP-Number, …)
// that client registrations are recorded against.
//
// Follows the prima-web mandatory form-modal blueprint (see CLAUDE.md):
//  - Internal formData state with refs for stable callbacks
//  - Initial snapshot for unsaved-changes (dirty) tracking
//  - Inline field validation via fieldErrors + getFieldError + clearFieldError
//  - 3-stage save flow: validate → ConfirmDialog → loading → toast
//  - Discard-changes ConfirmDialog on close when isDirty
//  - Minimize-to-bar support (chevronDown header button)
//
// Field set mirrors prima-api CreateRegistrationTypeRequest:
//   code              [Required, max 30, uppercase by convention]
//   name              [Required, max 100]
//   countryCode       [optional, 2 letters — blank = applies everywhere]
//   description       [optional]
//   formatPattern     [optional, max 200 — a regex, validated for compilability]
//   issuingAuthority  [optional, max 200]
//   displayOrder      [optional, >= 0]
//   statusCode        ("ACTIVE" or "INACTIVE")
//
// THE FORMAT PATTERN IS A BINDING RULE, NOT A HINT
//
// Whatever is entered here is enforced on every client registration of this
// type (fix 160). The live tester below exists because the cost of getting it
// wrong is asymmetric: too loose and a malformed GSTIN reaches a filed return;
// too tight and nobody can record a valid registration at all. Type a real
// number into the tester before saving the pattern.

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

export interface RegistrationTypeFormData {
  code: string;
  name: string;
  countryCode: string;
  description: string;
  formatPattern: string;
  issuingAuthority: string;
  displayOrder: string;
  statusCode: 'ACTIVE' | 'INACTIVE';
}

export interface CountryOption {
  value: string;
  label: string;
}

export interface RegistrationTypeFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData?: RegistrationTypeFormData | null;
  /** ISO alpha-2 options; the form adds its own "applies everywhere" entry. */
  countryOptions?: CountryOption[];
  countriesLoading?: boolean;
  isSaving?: boolean;
  apiError?: string | null;
  onSave: (data: RegistrationTypeFormData) => void;
  onClose: () => void;
  // ---- Minimize-to-bar support ----
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const INITIAL_FORM_DATA: RegistrationTypeFormData = {
  code: '',
  name: '',
  countryCode: '',
  description: '',
  formatPattern: '',
  issuingAuthority: '',
  displayOrder: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const ANY_COUNTRY_VALUE = '';

// Code pattern: letters, numbers, underscores, hyphens (matches the other masters)
const CODE_PATTERN = /^[A-Z0-9_-]+$/;

export const RegistrationTypeFormModal: React.FC<RegistrationTypeFormModalProps> = ({
  isOpen,
  mode,
  initialData,
  countryOptions = [],
  countriesLoading = false,
  isSaving = false,
  apiError,
  onSave,
  onClose,
  tabId,
  moduleName,
  onRestoreModal,
  createInstanceId,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<RegistrationTypeFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // Live pattern tester — not part of the saved record.
  const [patternSample, setPatternSample] = useState('');

  // --- Populate / reset form on open. Skip on restore. ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const src = initialDataRef.current;
    const data: RegistrationTypeFormData = src ? { ...src } : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    setPatternSample('');
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const setField = useCallback(
    (field: keyof RegistrationTypeFormData, value: string) => {
      clearFieldError(field as string);
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [clearFieldError]
  );

  const countrySelectOptions = useMemo(
    () => [
      { value: ANY_COUNTRY_VALUE, label: 'Applies everywhere (no country)' },
      ...countryOptions,
    ],
    [countryOptions]
  );

  /** null = no pattern or empty sample; true/false = the tester's verdict. */
  const patternVerdict = useMemo(() => {
    const pattern = formData.formatPattern.trim();
    if (!pattern || !patternSample) return null;
    try {
      return new RegExp(pattern).test(patternSample.trim().toUpperCase());
    } catch {
      return null;
    }
  }, [formData.formatPattern, patternSample]);

  const patternCompileError = useMemo(() => {
    const pattern = formData.formatPattern.trim();
    if (!pattern) return null;
    try {
      // eslint-disable-next-line no-new
      new RegExp(pattern);
      return null;
    } catch (e: any) {
      return e?.message || 'Not a valid regular expression';
    }
  }, [formData.formatPattern]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose, isSaving]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;

    const modalId =
      currentMode === 'create'
        ? `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`
        : `${tabId}-form-modal-edit-${currentFormData.code || 'new'}`;

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.code?.trim();
      const name = currentFormData.name?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.code || 'Registration Type'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentFormData.code : undefined
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.code.trim();
    const name = formData.name.trim();
    const country = formData.countryCode.trim();

    if (!code) {
      errors.code = 'Code is required';
    } else if (code.length > 30) {
      errors.code = 'Code must be 30 characters or less';
    } else if (!CODE_PATTERN.test(code)) {
      errors.code = 'Code can only contain uppercase letters, numbers, hyphens, and underscores';
    }

    if (!name) {
      errors.name = 'Name is required';
    } else if (name.length > 100) {
      errors.name = 'Name must be 100 characters or less';
    }

    if (country && !/^[A-Za-z]{2}$/.test(country)) {
      errors.countryCode = 'Country code must be 2 letters (ISO 3166-1 alpha-2)';
    }

    if (patternCompileError) {
      errors.formatPattern = `Not a valid regular expression: ${patternCompileError}`;
    } else if (formData.formatPattern.trim().length > 200) {
      errors.formatPattern = 'Format pattern must be 200 characters or less';
    }

    if (formData.displayOrder) {
      const order = Number(formData.displayOrder);
      if (!Number.isInteger(order) || order < 0) {
        errors.displayOrder = 'Display order must be zero or a positive whole number';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input, textarea')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, patternCompileError]);

  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    onSave({
      ...formData,
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
      countryCode: formData.countryCode.trim().toUpperCase(),
      description: formData.description.trim(),
      formatPattern: formData.formatPattern.trim(),
      issuingAuthority: formData.issuingAuthority.trim(),
      displayOrder: formData.displayOrder.trim(),
    });
  }, [formData, onSave]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add New Registration Type' : 'Edit Registration Type'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving} title="Close">
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          {apiError && (
            <div
              style={{
                marginBottom: '0.75rem',
                padding: '0.625rem 0.75rem',
                background: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                color: '#dc2626',
                fontSize: '0.8125rem',
                lineHeight: 1.4,
              }}
            >
              {apiError}
            </div>
          )}

          <div className="form-grid-2">
            <div className="form-group" data-field="code">
              <label className="form-label">
                Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('code') ? ' form-field-error' : ''}`}
                value={formData.code}
                maxLength={30}
                placeholder="e.g. GSTIN"
                disabled={isSaving || mode === 'edit'}
                onChange={(e) => setField('code', e.target.value.toUpperCase())}
              />
              {getFieldError('code') && (
                <div className="form-error-message">{getFieldError('code')}</div>
              )}
            </div>

            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                value={formData.statusCode}
                options={STATUS_OPTIONS}
                isClearable={false}
                isDisabled={isSaving}
                size="sm"
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    statusCode: (val as 'ACTIVE' | 'INACTIVE') || 'ACTIVE',
                  }))
                }
              />
            </div>
          </div>

          <div className="form-group" data-field="name">
            <label className="form-label">
              Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('name') ? ' form-field-error' : ''}`}
              value={formData.name}
              maxLength={100}
              placeholder="e.g. GSTIN"
              disabled={isSaving}
              onChange={(e) => setField('name', e.target.value)}
            />
            {getFieldError('name') && (
              <div className="form-error-message">{getFieldError('name')}</div>
            )}
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="countryCode">
              <SyutSelect
                label="Country"
                value={formData.countryCode}
                options={countrySelectOptions}
                isLoading={countriesLoading}
                isDisabled={isSaving}
                isClearable={false}
                isSearchable
                size="sm"
                error={getFieldError('countryCode')}
                onChange={(val) => setField('countryCode', (val as string) ?? '')}
              />
              {!getFieldError('countryCode') && (
                <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Only one “applies everywhere” type may exist per code
                </div>
              )}
            </div>

            <div className="form-group" data-field="displayOrder">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                min={0}
                className={`form-input${getFieldError('displayOrder') ? ' form-field-error' : ''}`}
                value={formData.displayOrder}
                placeholder="e.g. 20"
                disabled={isSaving}
                onChange={(e) => setField('displayOrder', e.target.value)}
              />
              {getFieldError('displayOrder') && (
                <div className="form-error-message">{getFieldError('displayOrder')}</div>
              )}
            </div>
          </div>

          <div className="form-group" data-field="issuingAuthority">
            <label className="form-label">Issuing Authority</label>
            <input
              type="text"
              className="form-input"
              value={formData.issuingAuthority}
              maxLength={200}
              placeholder="e.g. GST Network (GSTN)"
              disabled={isSaving}
              onChange={(e) => setField('issuingAuthority', e.target.value)}
            />
          </div>

          {/* Format pattern + live tester */}
          <div className="form-group" data-field="formatPattern">
            <label className="form-label">Format Pattern (regex)</label>
            <input
              type="text"
              className={`form-input${getFieldError('formatPattern') ? ' form-field-error' : ''}`}
              value={formData.formatPattern}
              maxLength={200}
              placeholder="^[A-Z]{5}[0-9]{4}[A-Z]$   — leave blank to accept anything"
              disabled={isSaving}
              onChange={(e) => setField('formatPattern', e.target.value)}
              style={{ fontFamily: 'var(--font-mono, monospace)' }}
            />
            {getFieldError('formatPattern') ? (
              <div className="form-error-message">{getFieldError('formatPattern')}</div>
            ) : (
              <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Enforced on every registration of this type. Blank means the format is not yet known.
              </div>
            )}

            {formData.formatPattern.trim() && !patternCompileError && (
              <div style={{ marginTop: '0.5rem' }}>
                <label className="form-label" style={{ fontSize: '0.75rem' }}>
                  Test it — type a real number
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    value={patternSample}
                    placeholder="e.g. 27AAPFU0939F1ZV"
                    disabled={isSaving}
                    onChange={(e) => setPatternSample(e.target.value.toUpperCase())}
                    style={{ fontFamily: 'var(--font-mono, monospace)', flex: 1 }}
                  />
                  {patternVerdict !== null && (
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        color: patternVerdict ? '#16a34a' : '#dc2626',
                      }}
                    >
                      {patternVerdict ? 'Matches' : 'No match'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              rows={2}
              placeholder="Optional description"
              disabled={isSaving}
              onChange={(e) => setField('description', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          mode === 'create'
            ? `Create registration type "${formData.code.trim().toUpperCase()}"?`
            : `Save changes to "${formData.code}"? The format pattern is applied to every registration of this type from now on.`
        }
        confirmLabel={mode === 'create' ? 'Create' : 'Save'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default RegistrationTypeFormModal;
